'use client';

import React, { useId, useState } from 'react';
import Link from '@/components/atoms/link';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { classnames } from '@/utils/classnames/classnames';
import Drawer from '@/components/organisms/drawer';
import type { CategoryNode, ProductCategory, SecondaryLink } from '../../types';

interface ProductsSlideMenuProps {
  open: boolean;
  productCategories: ProductCategory[];
  // Studio-driven utility links rendered beneath the category tree.
  secondaryLinks?: SecondaryLink[];
  onClose: () => void;
  // Stable id for the dialog panel so the "Shop Products" trigger can
  // reference it via aria-controls (accessible disclosure pattern).
  id?: string;
}

// Recursive category-node row. Renders a Link for leaves, an
// expand/collapse button for nodes with children. Indentation +
// font-size taper per depth so a 4- or 5-level tree stays scannable.
interface CategoryRowProps {
  node: CategoryNode;
  depth: number;
  onClose: () => void;
}

// Tailwind needs the full class string visible to its scanner, so we
// pick from a fixed map rather than computing `pl-${depth * 3}`.
const PADDING_LEFT_BY_DEPTH: Record<number, string> = {
  0: 'pl-0',
  1: 'pl-3',
  2: 'pl-6',
  3: 'pl-9',
  4: 'pl-12',
  5: 'pl-14',
};
const paddingLeft = (depth: number) => PADDING_LEFT_BY_DEPTH[depth] ?? 'pl-14';

const CategoryRow = ({ node, depth, onClose }: CategoryRowProps) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const translate = useTranslations();
  const hasChildren = !!node.children && node.children.length > 0;
  // Top-level (depth 0) uses the heaviest weight + a separator below
  // each entry. Deeper levels lighten as they go.
  const isTop = depth === 0;
  const containerClass = isTop ? 'border-b border-gray-100' : 'border-t border-gray-50';
  const linkSizeClass = depth === 0 ? 'text-14 py-2.5 font-medium' : 'text-13 py-2';
  const parentLinkSizeClass = depth === 0 ? 'text-14 py-2.5 font-medium' : 'text-13 py-2 font-medium';
  const colorClass = depth === 0 ? 'text-gray-700' : depth === 1 ? 'text-gray-600' : 'text-gray-500';
  const chevronClass = depth >= 2 ? 'size-3.5' : 'size-4';

  if (!hasChildren) {
    return (
      <div className={containerClass}>
        <Link
          href={node.href}
          block
          underlineOnHover={false}
          className={classnames(
            'hover:text-primary block transition-colors hover:bg-gray-50',
            linkSizeClass,
            colorClass,
            paddingLeft(depth),
          )}
          onClick={onClose}
        >
          {node.label}
        </Link>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className="flex w-full items-stretch">
        <Link
          href={node.href}
          block
          underlineOnHover={false}
          onClick={onClose}
          className={classnames(
            'hover:text-primary flex-1 transition-colors hover:bg-gray-50',
            parentLinkSizeClass,
            colorClass,
            paddingLeft(depth),
          )}
        >
          {node.label}
        </Link>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={translate(open ? 'common.collapse-category' : 'common.expand-category', {
            label: node.label,
          })}
          className="hover:text-primary flex shrink-0 items-center px-4 transition-colors hover:bg-gray-50"
        >
          <ChevronRightIcon
            className={classnames('text-gray-400 transition-transform duration-200', chevronClass, {
              'rotate-90': open,
            })}
            aria-hidden
          />
        </button>
      </div>
      {open && (
        <div id={panelId} className="pb-1">
          {node.children!.map((child) => (
            <CategoryRow key={child.label + child.href} node={child} depth={depth + 1} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  );
};

// EWM-294 — the "Shop Products" category panel that opens from the header on
// desktop and mobile. Built on the shared Drawer organism (matching the
// EWM-295 MobileMenu refactor) so it inherits one canonical implementation of
// the overlay, focus-trap, focus-return-to-trigger, body-scroll-block, the
// XMark close button and the `role="dialog"`/`aria-modal`/`aria-labelledby`
// contract — rather than hand-rolling each. `direction="left"` gives the
// slide-in-from-left motion the v0 mock specifies.
const ProductsSlideMenu = ({ open, productCategories, secondaryLinks, onClose, id }: ProductsSlideMenuProps) => {
  const translate = useTranslations();

  return (
    <Drawer
      isOpen={open}
      direction="left"
      headline={translate('common.products')}
      onClose={onClose}
      id={id}
      // EWM-658 — Dan's v0 style guide insets this menu at px-4 (16px). Override
      // the shared Drawer's default px-6 (24px) header so the heading lines up
      // with the category list below. `!px-4` beats the hardcoded px-6.
      headerClassName="!px-4"
    >
      <nav className="px-4 py-2">
        {productCategories.map((category) => (
          <CategoryRow key={category.label + category.href} node={category} depth={0} onClose={onClose} />
        ))}
      </nav>
      {secondaryLinks && secondaryLinks.length > 0 && (
        <ul className="mt-1 border-t border-gray-200 px-4 pt-2">
          {secondaryLinks.map((link) => (
            <li key={link.label + link.href}>
              <Link
                href={link.href}
                block
                underlineOnHover={false}
                onClick={onClose}
                className="text-14 hover:text-primary block py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
};

export default ProductsSlideMenu;
